import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { meuPainelKeys } from './me-painel.hooks';

import { api } from '@/lib/api-client';
import type { Papel } from '@/lib/etapas-labels';

/**
 * Hooks de pendências centralizadas (Sprint 16 + Sprint 18).
 *
 * - `usePendencias` — lista do usuário logado. Sprint 18: polling de 5min
 *   (com `refetchIntervalInBackground: false` — pausa em aba inativa) para
 *   alimentar `<BadgePendencias>` com notificações de novas pendências.
 *   Diverge do padrão "sem polling" do ADR 0016 §2.10 por escolha do PO —
 *   trade-off aceito: 5min de latência em troca de zero infra de eventos.
 * - `useResolverPendencia` — marca pendência como resolvida. Invalida tanto
 *   `pendencias` quanto `me/painel`.
 */

export interface Pendencia {
  id: string;
  tipo: string;
  descricao: string;
  criadaEm: string;
  papelAlvo: Papel | null;
  usuarioAlvoId: string | null;
  os: { id: string; codigo: string; nomeProjeto: string } | null;
}

export interface PendenciasFiltros {
  tipo?: string;
  osId?: string;
}

export const pendenciasKeys = {
  all: ['pendencias'] as const,
  lista: (f: PendenciasFiltros) => ['pendencias', 'lista', f.tipo ?? '', f.osId ?? ''] as const,
  contagem: ['pendencias', 'contagem'] as const,
};

export function usePendencias(filtros: PendenciasFiltros = {}) {
  return useQuery({
    queryKey: pendenciasKeys.lista(filtros),
    queryFn: async () => {
      const search = new URLSearchParams();
      if (filtros.tipo) search.set('tipo', filtros.tipo);
      if (filtros.osId) search.set('osId', filtros.osId);
      const qs = search.toString();
      const { data } = await api.get<Pendencia[]>(`/pendencias${qs ? `?${qs}` : ''}`);
      return data;
    },
    // Página /pendencias: dados frescos quando o usuário entra (sem polling
    // aqui — o polling vive em `usePendenciasContagem` agora, R-2.12).
    staleTime: 60 * 1000,
  });
}

/**
 * Hook dedicado para o badge da sidebar e do header (Lote D / R-2.12).
 *
 * Substitui o uso de `usePendencias({}).data.length` que trafegava a lista
 * inteira a cada polling. Agora o backend retorna `{ total: number }` —
 * apenas 1 inteiro pelo fio.
 *
 * Polling de 5min mantido (ADR 0017 — exceção pontual ao ADR 0016 §2.10).
 * `refetchIntervalInBackground: false` pausa quando a aba está inativa.
 */
export function usePendenciasContagem() {
  return useQuery({
    queryKey: pendenciasKeys.contagem,
    queryFn: async () => {
      const { data } = await api.get<{ total: number }>('/pendencias/contagem');
      return data.total;
    },
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });
}

export function useResolverPendencia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, observacao }: { id: string; observacao?: string }) => {
      const { data } = await api.post<{ id: string; resolvidaEm: string }>(
        `/pendencias/${id}/resolver`,
        observacao ? { observacao } : {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pendenciasKeys.all });
      void qc.invalidateQueries({ queryKey: meuPainelKeys.all });
    },
  });
}
